import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import FieldSet from "@infotech/uikit-fieldset"
import { Field } from "redux-form"
import { Flex, Box } from "@infotech/uikit-grid"
import DatePicker from "@infotech/uikit-datepicker"
import classnames from "classnames"
import moment from "moment"
import "moment-precise-range-plugin"
import { getTestAttribute } from "src/containers/Plans/Item"
import PlanDuration from "./planDurationPicker"
import getAddDateUnit from "./helper"
import styles from "./styles.css"

const getCurrentTime = () =>
  moment()
    .set({
      h: 0,
      m: 0,
      s: 0,
      ms: 0,
    })
    .valueOf()

// Метод расчета типа длительности по убыванию от Года до Дня
const calculateDuration = (
  startDateTime,
  endDateTime,
  setDurationType,
  setDurationCount,
) => {
  const difference = moment.preciseDiff(startDateTime, endDateTime, true)
  const getDuration = duration =>
    moment(endDateTime).diff(moment(startDateTime), duration, true)
  switch (difference) {
    case difference.years !== 0 &&
      difference.days === 0 &&
      difference.months === 0: {
      setDurationType(`YEAR`)
      setDurationCount(getDuration(`YEAR`))
      break
    }
    case difference.years === 0 &&
      difference.days === 0 &&
      difference.months !== 0: {
      setDurationType(`MONTH`)
      setDurationCount(getDuration(`MONTH`))
      break
    }
    case difference.days !== 0: {
      const currentWeek = getDuration(`DAY`) % 7 === 0
      if (currentWeek) {
        setDurationType(`WEEK`)
        setDurationCount(getDuration(`WEEK`))
      } else {
        setDurationType(`DAY`)
        setDurationCount(getDuration(`DAY`))
      }
      break
    }
    default:
      break
  }
  if (
    difference.years !== 0 &&
    difference.days === 0 &&
    difference.months === 0
  ) {
    setDurationType(`YEAR`)
    setDurationCount(getDuration(`YEAR`))
  }
  if (
    difference.years === 0 &&
    difference.days === 0 &&
    difference.months !== 0
  ) {
    setDurationType(`MONTH`)
    setDurationCount(getDuration(`MONTH`))
  }
  if (difference.days !== 0) {
    const currentWeek = getDuration(`DAY`) % 7 === 0
    if (currentWeek) {
      setDurationType(`WEEK`)
      setDurationCount(getDuration(`WEEK`))
    } else {
      setDurationType(`DAY`)
      setDurationCount(getDuration(`DAY`))
    }
  }
}

const DateTimePicker = ({
  input: { value, onChange, name },
  meta: { error },
  duration,
  endDateTime,
  change,
  durationType,
  durationCount,
  startDateTime,
  setDurationType,
  setDurationCount,
  endDateValue,
  changeEndDateValue,
  isDisabled,
}) => (
  <div data-testid={getTestAttribute(name)}>
    <DatePicker
      onChange={val => {
        if (name === `startDateTime` && val) {
          const newEndDateValue = moment(val)
            .add(durationCount, getAddDateUnit(durationType))
            .valueOf()
          change(`endDateTime`, newEndDateValue)
          changeEndDateValue(newEndDateValue - 86400000)
          return onChange(val.getTime())
        }
        if (name === `endDateTime` && val) {
          calculateDuration(
            startDateTime,
            val.getTime() + 86400000,
            setDurationType,
            setDurationCount,
            name,
          )
          changeEndDateValue(val.getTime())
          return onChange(val.getTime() + 86400000)
        }
        return val && onChange(val.getTime())
      }}
      name={name}
      withTime={false}
      duration={duration}
      endDateTime={endDateTime}
      value={name === `endDateTime` ? new Date(endDateValue) : new Date(value)}
      className={classnames(styles[`date-time-picker`], {
        [styles[`red-border`]]: error,
      })}
      disabled={isDisabled}
    />
    <Box loose className={styles[`input-error`]}>
      {error}
    </Box>
  </div>
)

const PlanDurationPicker = ({
  startDateTime,
  endDateTime,
  pending,
  change,
  isDisabled,
  isNew,
}) => {
  const [durationType, setDurationType] = useState(`DAY`)
  const [durationCount, setDurationCount] = useState(1)
  const [endDateValue, changeEndDateValue] = useState(
    isNew ? getCurrentTime() : startDateTime,
  )
  useEffect(() => {
    calculateDuration(
      startDateTime,
      endDateTime,
      setDurationType,
      setDurationCount,
    )
    if (!isNew) changeEndDateValue(endDateTime - 86400000)
  }, [pending])
  return (
    <Flex column>
      <FieldSet caption="Начало плана" required>
        <div data-testid="plans-start-date-time">
          <Field
            component={DateTimePicker}
            name="startDateTime"
            props={{
              startDateTime,
              change,
              durationType,
              durationCount,
              setDurationType,
              changeEndDateValue,
              isDisabled,
            }}
          />
        </div>
      </FieldSet>
      <FieldSet caption="Длительность плана" required>
        <PlanDuration
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          pending={pending}
          change={change}
          durationType={durationType}
          setDurationType={setDurationType}
          durationCount={durationCount}
          setDurationCount={setDurationCount}
          changeEndDateValue={changeEndDateValue}
          isDisabled={isDisabled}
        />
      </FieldSet>
      <FieldSet caption="Окончание плана" required>
        <div data-testid="plans-end-date-time">
          <Field
            component={DateTimePicker}
            name="endDateTime"
            props={{
              startDateTime,
              change,
              durationType,
              durationCount,
              setDurationType,
              setDurationCount,
              endDateValue,
              changeEndDateValue,
              isDisabled,
            }}
          />
        </div>
      </FieldSet>
    </Flex>
  )
}

PlanDurationPicker.propTypes = {
  startDateTime: PropTypes.number.isRequired,
  change: PropTypes.func.isRequired,
  endDateTime: PropTypes.number.isRequired,
  pending: PropTypes.bool.isRequired,
  isNew: PropTypes.bool.isRequired,
}

export default PlanDurationPicker
